import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navItems = [
    { name: '홈', path: '/' },
    {
      name: '기업정보',
      path: '/company',
      dropdown: [
        { name: '회사 소개', path: '/about' },
        { name: '연혁', path: '/history' },
        { name: '인증', path: '/certifications' },
        { name: '조직현황', path: '/organization' },
        { name: '오시는 길', path: '/location' },
      ]
    },
    {
      name: '제품소개',
      path: '/products',
      dropdown: [
        { name: 'Harness', path: '/harness' },
        { name: '전장박스', path: '/controlbox' },
      ]
    },
    {
      name: '제조환경',
      path: '/manufacturing',
      dropdown: [
        { name: '제조실', path: '/manufacturing' },
      ]
    },
  ];

  return (
    <>
      <header
        className={`fixed w-full z-50 transition-all duration-300 ${isScrolled
          ? 'bg-white/80 backdrop-blur-md shadow-sm py-3 border-b border-slate-100' // Scrolled: Glassmorphic white
          : 'bg-transparent py-5' // Top: Fully transparent
          }`}
      >
        <div className="section-container flex justify-between items-center relative">
          {/* Gradient Overlay for better readability at top if needed, can be added as absolute div but simpler to just ensure text is readable */}

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="p-1 rounded-sm">
              <img
                src={`${process.env.PUBLIC_URL}/images/pt_logo.png`}
                alt="Power Tech"
                className={`h-8 w-auto object-contain transition-all duration-300 ${!isScrolled ? 'brightness-0 invert' : ''}`} // Invert logo to white at top if it's dark, assuming original is dark. If original is colored, maybe keep as is or adjust. Let's assume user wants white text/logo at top.
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  if (target.parentNode) {
                    (target.parentNode as HTMLElement).classList.add('hidden');
                  }
                }}
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <div
                key={item.name}
                className="relative group h-full"
                onMouseEnter={() => setActiveDropdown(item.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {item.dropdown ? (
                  <button className={`flex items-center space-x-1 font-medium text-sm font-bold tracking-wide transition-colors duration-200 ${isScrolled ? 'text-primary hover:text-primary' : 'text-white/90 hover:text-white'
                    }`}>
                    <span>{item.name}</span>
                    <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" />
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className={`font-medium text-sm font-bold tracking-wide transition-colors duration-200 ${isScrolled ? 'text-primary hover:text-primary' : 'text-white/90 hover:text-white'
                      }`}
                  >
                    {item.name}
                  </Link>
                )}

                {/* Dropdown */}
                {item.dropdown && (
                  <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 w-48 transition-all duration-200 ${activeDropdown === item.name ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'
                    }`}>
                    <div className="bg-white rounded-lg shadow-xl border border-slate-100 overflow-hidden py-1">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          className="block px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA & Mobile Toggle */}
          <div className="flex items-center space-x-4">
            <Link
              to="/quote"
              className={`hidden md:inline-flex items-center justify-center px-5 py-2 text-sm font-semibold transition-all duration-300 rounded-full ${isScrolled
                ? 'bg-primary text-white hover:bg-primary-light shadow-lg hover:shadow-primary/30'
                : 'bg-white/20 text-white backdrop-blur-sm hover:bg-white/30 border border-white/40'
                }`}
            >
              견적문의
            </Link>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-md transition-colors ${isScrolled ? 'text-slate-800' : 'text-white'
                }`}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-white transform transition-transform duration-300 ease-in-out md:hidden ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        style={{ top: '0', paddingTop: '80px' }}
      >
        <div className="p-6 space-y-6 overflow-y-auto h-full pb-20">
          {navItems.map((item) => (
            <div key={item.name} className="space-y-3">
              {item.dropdown ? (
                <>
                  <div className="text-lg font-bold text-slate-800 border-b border-slate-100 pb-2">
                    {item.name}
                  </div>
                  <div className="pl-4 space-y-3 flex flex-col">
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.path}
                        className="text-slate-600 hover:text-primary transition-colors"
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <Link
                  to={item.path}
                  className="block text-lg font-bold text-slate-800 border-b border-slate-100 pb-2"
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}

          <div className="pt-6">
            <Link
              to="/quote"
              className="w-full flex justify-center py-3 bg-primary text-white font-bold rounded-lg shadow-lg"
            >
              견적 문의하기
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
